import React from 'react';
import { Query } from '@apollo/client/react/components';

// This component works w/ GraphQL connections, the following must be provided in the query:
// edges + nodes, each node must have an ID
// pageInfo w/ endCursor and hasNextPage
// queryName
// e.g.
// in the example below, "currentRanksForCategory" is the queryName
// query {
//   currentRanksForCategory(category: $category, first: $pageSize, after: $afterCursor) {
//     edges {
//       node {
//         ...RankCard
//       }
//     }
//     pageInfo {
//       endCursor
//       hasNextPage
//     }
//   }
// }

// see https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-config-options-fetchPolicy
// for what fetchpolicy does
// default fetch policy is cache_first
export const FETCH_POLICIES = {
  NETWORK_ONLY: 'network-only',
  CACHE_FIRST: 'cache-first'
};

const pageInfoAlreadyRetrieved = (previousEdges, newEdges) => {
  const mostRecentID = previousEdges[previousEdges.length - 1].node.id;

  if (newEdges[newEdges.length - 1].node.id === mostRecentID) {
    // page info already retrieved
    return true;
  }
  return false;
};

const fetchNextPage = ({
  query,
  queryName,
  secondaryField,
  endCursor,
  fetchMore,
  variables,
  pageSize = 10
}) => {
  return () => {
    return fetchMore({
      query,
      variables: {
        ...variables,
        afterCursor: endCursor,
        pageSize
      },
      updateQuery: (previousResults, newResults) => {
        const { fetchMoreResult } = newResults;

        const previousEdges = previousResults[queryName].edges;
        const newEdges = fetchMoreResult[queryName].edges;
        const newPageInfo = fetchMoreResult[queryName].pageInfo;

        if (pageInfoAlreadyRetrieved(previousEdges, newEdges)) {
          return previousResults;
        }

        const mergedResults = {
          [queryName]: {
            edges: [...previousEdges, ...newEdges],
            pageInfo: newPageInfo,
            __typename: previousResults[queryName].__typename
          }
        };
        const secondaryResults = secondaryField
          ? {
              [secondaryField]: previousResults[secondaryField]
            }
          : null;
        return { ...mergedResults, ...secondaryResults };
      }
    });
  };
};

const PaginatedIndexQueryContainer = ({
  query,
  queryName,
  secondaryField,
  pageSize,
  variables,
  fetchPolicy,
  children
}) => {
  return (
    <Query
      query={query}
      variables={{ afterCursor: null, pageSize, ...variables }}
      fetchPolicy={fetchPolicy ? fetchPolicy : FETCH_POLICIES.CACHE_FIRST}
    >
      {({ loading, data, fetchMore, ...props }) => {
        if (loading || !data) {
          return children({ loading: true });
        }

        if (!data[queryName]) {
          // no data
          return children({ loading: false, data, ...props });
        }

        const indexItems = data[queryName].edges.map(edge => edge.node);
        const endCursor = data[queryName].pageInfo.endCursor;
        const hasNextPage = data[queryName].pageInfo.hasNextPage;

        return children({
          loading: false,
          data: {
            ...data,
            indexItems,
            hasNextPage,
            fetchNextPage: fetchNextPage({
              query,
              queryName,
              secondaryField,
              variables,
              endCursor,
              pageSize,
              fetchMore
            })
          },
          ...props
        });
      }}
    </Query>
  );
};

export default PaginatedIndexQueryContainer;
